import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Mail, Calendar, LogOut } from "lucide-react";

export const Route = createFileRoute("/_main/profile")({
  component: ProfilePage,
});

function ProfilePage() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        <h1 className="mb-6 text-2xl font-bold text-zinc-900 dark:text-zinc-50">
          {t("profile.title")}
        </h1>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t("profile.account_info")}</CardTitle>
              <CardDescription>{t("profile.account_info_desc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-zinc-400" />
                <div>
                  <p className="text-xs text-zinc-500">{t("profile.email")}</p>
                  <p className="text-sm font-medium">{user.email}</p>
                </div>
              </div>

              {user.role === "admin" && (
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-zinc-400" />
                  <div>
                    <Badge variant="default">{t("nav.admin_role")}</Badge>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-zinc-400" />
                <div>
                  <p className="text-xs text-zinc-500">{t("profile.joined")}</p>
                  <p className="text-sm font-medium">
                    {user.created_at
                      ? new Date(user.created_at).toLocaleDateString("ko-KR")
                      : "-"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="py-4">
              <Button
                variant="destructive"
                className="w-full cursor-pointer"
                onClick={() => {
                  logout();
                  navigate({ to: "/login" });
                }}
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t("nav.logout")}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
